import { ReactNode } from "react";
import { Link } from "react-router-dom";
import {
  Bell, Settings as SettingsIcon, Plus, Globe, Moon,
  Shield, FileText, RotateCcw, Info, Trash2, Loader2, ChevronDown,
  Zap, TrendingUp, Ruler, LifeBuoy, Mail, MessageCircle, BarChart3,
  Save, Flag, Star, Megaphone, ShieldAlert,
} from "lucide-react";
import { cn } from "@/lib/utils";
import type { Region } from "@/hooks/useRegion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { LANGUAGES } from "@/lib/languages";

import { formatCredits } from "@/lib/format-credits";
import { StitchDesktopHeader } from "@/components/stitch/StitchDesktopHeader";
import { CreditsUsageCard } from "@/components/CreditsUsageCard";
import { BroadcastBanner } from "@/components/BroadcastBanner";
import { UserTierBadge } from "@/components/UserTierBadge";

const GENDERS = ["Female", "Male", "Non-binary", "Prefer not to say"];
const BODY_TYPES = ["Slim", "Athletic", "Average", "Curvy", "Plus"];
const SKIN_TONES = ["Fair", "Light", "Medium", "Tan", "Brown", "Deep"];
const STYLES = ["Casual", "Streetwear", "Formal", "Party", "Sporty", "Minimal", "Boho", "Vintage"];
const COLORS = ["Black", "White", "Neutral", "Pastel", "Bright", "Earth", "Jewel", "Monochrome"];

interface Props {
  // Identity
  displayName: string;
  email: string;
  avatarUrl: string;
  initials: string;
  credits: number | null;
  tryonCount: number | null;

  // Preferences
  region: Region;

  // Auth & navigation
  onSignOut: () => void;
  onBuyCredits: () => void;
  onViewPurchases: () => void;

  // Body specs (form state)
  setDisplayName: (v: string) => void;
  gender: string;        setGender: (v: string) => void;
  bodyType: string;      setBodyType: (v: string) => void;
  skinTone: string;      setSkinTone: (v: string) => void;
  heightCm: string;      setHeightCm: (v: string) => void;
  size: string;          setSize: (v: string) => void;
  styles: string[];      setStyles: (v: string[]) => void;
  colors: string[];      setColors: (v: string[]) => void;
  onSaveSpecs: () => void;
  saving: boolean;

  // Language preference
  language: string;
  onLanguageChange: (next: string) => void;

  // Support handlers (intercepted on desktop)
  onContactEmail: () => void;
  onContactWhatsApp: () => void;
  onContactFeedback: () => void;

  // Admin
  isAdmin: boolean;

  // Dev
  onResetOnboarding: () => void;

  // Danger zone
  onDelete: () => void;
  deleting: boolean;
  confirmText: string;
  setConfirmText: (s: string) => void;
}

/**
 * Desktop "Personal Identity" page — Stitch reference layout, but containing
 * every section from the mobile Profile page (stats, leaderboard, body specs,
 * support, admin, dev tools, danger zone). Logic-free: receives all handlers
 * + state from Profile.tsx.
 */
export const ProfileDesktop = (props: Props) => {
  const {
    displayName, email, avatarUrl, initials, credits, tryonCount,
    region,
    onSignOut, onBuyCredits, onViewPurchases,
    setDisplayName,
    gender, setGender, bodyType, setBodyType, skinTone, setSkinTone,
    heightCm, setHeightCm, size, setSize, styles, setStyles, colors, setColors,
    onSaveSpecs, saving,
    language, onLanguageChange,
    onContactEmail, onContactWhatsApp, onContactFeedback,
    isAdmin, onResetOnboarding,
    onDelete, deleting, confirmText, setConfirmText,
  } = props;

  const regionLabel = region === "in" ? "India · INR" : "International · USD";

  const toggle = (arr: string[], v: string, setter: (s: string[]) => void) =>
    setter(arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);

  return (
    <div className="relative min-h-screen">
      {/* Top bar — eyebrow + title + credits + bell + avatar */}
      <StitchDesktopHeader
        eyebrow="Identity Settings"
        title="Profile"
        onBuyCredits={onBuyCredits}
      />

      <div className="mx-auto w-full max-w-5xl px-8 py-10">
        {/* Eyebrow + heading */}
        <div>
          <span className="font-display text-[11px] font-semibold uppercase tracking-[0.32em] text-primary/80">
            — Profile Settings
          </span>
          <h1 className="font-display mt-3 text-4xl font-bold tracking-tight text-foreground md:text-5xl">
            Personal Identity
          </h1>
        </div>

        <div className="mt-8 grid gap-5 lg:grid-cols-3">
          {/* ===== LEFT COLUMN (Identity, Balance, Stats, Specs, Support) ===== */}
          <div className="space-y-5 lg:col-span-2">
            {/* User card */}
            <Section>
              <div className="flex items-center gap-4">
                <div className="flex h-16 w-16 items-center justify-center overflow-hidden rounded-full border-2 border-primary/40 bg-gradient-to-br from-primary/30 to-accent/20 font-display text-lg font-bold">
                  {avatarUrl ? (
                    <img src={avatarUrl} alt={displayName || email} referrerPolicy="no-referrer" className="h-full w-full object-cover" />
                  ) : initials}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-display truncate text-lg font-bold text-foreground">
                    {displayName || "User Name"}
                  </p>
                  <p className="truncate text-sm text-muted-foreground">{email}</p>
                  <div className="mt-2">
                    <UserTierBadge size="sm" />
                  </div>
                </div>
                <Button
                  variant="outline"
                  onClick={onSignOut}
                  className="rounded-full border-white/10 bg-white/[0.03] px-5 hover:border-destructive/40 hover:bg-destructive/10 hover:text-destructive"
                >
                  Sign out
                </Button>
              </div>
            </Section>

            {/* Announcements */}
            <BroadcastBanner />

            {/* Stat cards — Credits + Try-Ons */}
            <div className="grid grid-cols-2 gap-3">
              <StatCard
                icon={<Zap className="h-5 w-5" strokeWidth={2.5} />}
                value={formatCredits(credits)}
                label="Credits Left"
                tone="primary"
              />
              <StatCard
                icon={<TrendingUp className="h-5 w-5" strokeWidth={2.5} />}
                value={tryonCount ?? 0}
                label="Try-Ons Made"
                tone="accent"
              />
            </div>

            {/* Balance card */}
            <Section>
              <div className="flex items-center justify-between gap-6">
                <div>
                  <p className="font-display text-[10px] font-semibold uppercase tracking-[0.3em] text-muted-foreground">
                    Current Balance
                  </p>
                  <div className="mt-2 flex items-baseline gap-3">
                    <span className="font-display text-5xl font-bold tabular-nums text-foreground">
                      {formatCredits(credits)}
                    </span>
                    <span className="text-sm text-muted-foreground">Tokens available</span>
                  </div>
                  <button
                    type="button"
                    onClick={onViewPurchases}
                    className="mt-2 text-xs text-accent underline-offset-4 hover:underline"
                  >
                    View purchase history
                  </button>
                </div>
                <Button
                  onClick={onBuyCredits}
                  className="font-display group relative h-12 overflow-hidden rounded-full bg-gradient-to-r from-primary via-primary to-accent px-7 text-sm font-bold tracking-wide text-primary-foreground shadow-[0_8px_30px_-8px_hsl(var(--primary))] transition-all hover:shadow-[0_12px_40px_-8px_hsl(var(--primary))]"
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Buy more credits
                </Button>
              </div>
            </Section>

            {/* Credits usage breakdown */}
            <CreditsUsageCard onBuyCredits={onBuyCredits} />

            {/* Body Measurement Specs */}
            <Section>
              <SectionHeading
                icon={<Ruler className="h-4 w-4" />}
                title="Body Measurement Specs"
                subtitle="Help our AI tailor try-ons to your style and proportions."
              />

              <div className="mt-5 space-y-5">
                <div className="space-y-1.5">
                  <Label htmlFor="profile-display-name-desktop">Display name</Label>
                  <Input
                    id="profile-display-name-desktop"
                    name="displayName"
                    autoComplete="name"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    maxLength={60}
                  />
                </div>

                <FieldGroup label="Gender">
                  {GENDERS.map((g) => (
                    <Pill key={g} active={gender === g} onClick={() => setGender(g)}>{g}</Pill>
                  ))}
                </FieldGroup>

                <FieldGroup label="Body type">
                  {BODY_TYPES.map((b) => (
                    <Pill key={b} active={bodyType === b} onClick={() => setBodyType(b)}>{b}</Pill>
                  ))}
                </FieldGroup>

                <FieldGroup label="Skin tone">
                  {SKIN_TONES.map((s) => (
                    <Pill key={s} active={skinTone === s} onClick={() => setSkinTone(s)}>{s}</Pill>
                  ))}
                </FieldGroup>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="profile-height-cm-desktop">Height (cm)</Label>
                    <Input
                      id="profile-height-cm-desktop"
                      name="heightCm"
                      autoComplete="off"
                      type="number"
                      inputMode="numeric"
                      value={heightCm}
                      onChange={(e) => setHeightCm(e.target.value)}
                      min={100}
                      max={230}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="profile-size-desktop">Size</Label>
                    <Input
                      id="profile-size-desktop"
                      name="size"
                      autoComplete="off"
                      value={size}
                      onChange={(e) => setSize(e.target.value)}
                      maxLength={6}
                    />
                  </div>
                </div>

                <FieldGroup label="Favorite styles">
                  {STYLES.map((s) => (
                    <Pill key={s} active={styles.includes(s)} onClick={() => toggle(styles, s, setStyles)}>{s}</Pill>
                  ))}
                </FieldGroup>

                <FieldGroup label="Color preferences">
                  {COLORS.map((c) => (
                    <Pill key={c} active={colors.includes(c)} onClick={() => toggle(colors, c, setColors)}>{c}</Pill>
                  ))}
                </FieldGroup>

                <Button
                  onClick={onSaveSpecs}
                  disabled={saving}
                  className="font-display w-full bg-gradient-to-r from-primary to-primary/80 text-xs font-bold uppercase tracking-[0.22em] text-primary-foreground shadow-[0_0_24px_-6px_hsl(var(--primary))] hover:opacity-95 sm:w-auto sm:px-8"
                >
                  {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
                  Save Specs
                </Button>
              </div>
            </Section>

            {/* Support */}
            <Section>
              <SectionHeading
                icon={<LifeBuoy className="h-4 w-4" />}
                title="Help & Support"
                subtitle="We typically reply within 1–2 business days."
              />
              <div className="mt-4 grid gap-2 sm:grid-cols-2">
                <SupportRow
                  icon={<Mail className="h-4 w-4" />}
                  title="Email support"
                  subtitle="support@fitauraai.com"
                  onClick={onContactEmail}
                />
                <SupportRow
                  icon={<MessageCircle className="h-4 w-4" />}
                  title="WhatsApp support"
                  subtitle="Chat with us directly"
                  onClick={onContactWhatsApp}
                />
                <SupportRow
                  icon={<MessageCircle className="h-4 w-4" />}
                  title="Send feedback"
                  subtitle="Ideas to make FitAura AI better"
                  onClick={onContactFeedback}
                />
                <SupportRow
                  icon={<Flag className="h-4 w-4" />}
                  title="Report content"
                  subtitle="Flag offensive AI content (WhatsApp)"
                  onClick={onContactFeedback}
                />
                <SupportRow
                  icon={<Star className="h-4 w-4" />}
                  title="Rate us on Play Store"
                  subtitle="Leave a 5-star rating ⭐"
                  onClick={() => window.open("https://play.google.com/store/apps/details?id=com.fitauraai.app", "_blank", "noopener,noreferrer")}
                />
                {isAdmin && (
                  <SupportRow
                    icon={<BarChart3 className="h-4 w-4" />}
                    title="Admin console"
                    subtitle="Revenue, users, ops, mobile"
                    to="/admin"
                  />
                )}
              </div>
            </Section>
          </div>

          {/* ===== RIGHT COLUMN (Preferences, Leaderboard, Footer links, Dev, Danger) ===== */}
          <div className="space-y-5">
            {/* System preferences */}
            <Section>
              <h2 className="font-display text-xs font-bold uppercase tracking-[0.28em] text-foreground">
                System Preferences
              </h2>

              <div className="mt-5 space-y-3">
                {/* Theme picker removed — app is intentionally always-dark. */}

                {/* Region */}
                <div className="flex items-center justify-between rounded-xl border border-white/10 bg-white/[0.02] px-4 py-3">
                  <div className="flex items-center gap-3">
                    <Globe className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm font-medium">Region</span>
                  </div>
                  <span className="font-display text-[10px] font-semibold uppercase tracking-[0.22em] text-muted-foreground">
                    {regionLabel}
                  </span>
                </div>
              </div>

            </Section>

            {/* Quick links footer */}
            <Section>
              <div className="grid grid-cols-4 gap-2">
                <FooterLink to="/privacy" icon={<Shield className="h-4 w-4" />} label="Privacy" />
                <FooterLink to="/terms" icon={<FileText className="h-4 w-4" />} label="Terms" />
                <FooterLink to="/refund" icon={<RotateCcw className="h-4 w-4" />} label="Refunds" />
                <FooterLink to="/about" icon={<Info className="h-4 w-4" />} label="About" />
              </div>
            </Section>

            {/* Dev tools (only in dev) */}
            {import.meta.env.DEV && (
              <Section className="border-primary/30">
                <h3 className="font-display text-xs font-bold uppercase tracking-[0.22em] text-primary">
                  Dev Tools
                </h3>
                <p className="mt-1 text-xs text-muted-foreground">
                  Re-run the onboarding flow for this account.
                </p>
                <Button
                  variant="outline"
                  className="mt-3 w-full border-primary/40 text-primary hover:bg-primary/10"
                  onClick={onResetOnboarding}
                >
                  <RotateCcw className="mr-2 h-4 w-4" />
                  Reset onboarding
                </Button>
              </Section>
            )}

            {/* Danger zone */}
            <Section className="border-destructive/30 bg-destructive/[0.03]">
              <h3 className="font-display text-base font-bold text-destructive">
                Danger Zone
              </h3>
              <p className="mt-1 text-sm text-muted-foreground">
                Permanently delete your account and all associated style data.
              </p>
              <AlertDialog onOpenChange={(o) => !o && setConfirmText("")}>
                <AlertDialogTrigger asChild>
                  <Button
                    variant="outline"
                    disabled={deleting}
                    className="mt-3 w-full border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive"
                  >
                    {deleting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Trash2 className="mr-2 h-4 w-4" />}
                    Delete account
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Delete your FitAura AI account?</AlertDialogTitle>
                    <AlertDialogDescription>
                      This permanently removes your profile, all uploaded photos, try-on history, and remaining credits. This action is irreversible.
                      <br /><br />
                      Type <span className="font-mono font-bold text-foreground">DELETE</span> below to confirm.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <Input
                    id="profile-delete-confirm-desktop"
                    name="deleteConfirm"
                    autoComplete="off"
                    value={confirmText}
                    onChange={(e) => setConfirmText(e.target.value)}
                    placeholder="DELETE"
                    autoFocus
                  />
                  <AlertDialogFooter>
                    <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={(e) => { e.preventDefault(); onDelete(); }}
                      disabled={deleting || confirmText !== "DELETE"}
                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    >
                      {deleting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                      Permanently delete
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </Section>
          </div>
        </div>

        {/* Footer stats */}
        <div className="font-display mt-12 gap-6 flex-row flex items-center justify-start">
          <div className="flex items-center justify-center gap-12 text-center">
            <div>
              <p className="text-xl font-bold text-foreground">24.2k</p>
              <p className="mt-1 text-[10px] font-semibold uppercase tracking-[0.28em] text-muted-foreground">
                Global Users
              </p>
            </div>
            <div>
              <p className="text-xl font-bold text-foreground">1.4M</p>
              <p className="mt-1 text-[10px] font-semibold uppercase tracking-[0.28em] text-muted-foreground">
                Styles Rendered
              </p>
            </div>
          </div>
          <p className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground/70">
            FitAura AI · v1.0.0
          </p>
        </div>
      </div>
    </div>
  );
};

/* ---------- Local helpers ---------- */

const Section = ({ children, className }: { children: ReactNode; className?: string }) => (
  <section className={cn("rounded-2xl border border-white/10 bg-card/40 p-6 backdrop-blur-xl", className)}>
    {children}
  </section>
);

const SectionHeading = ({
  icon, title, subtitle,
}: { icon: ReactNode; title: string; subtitle?: string }) => (
  <div className="flex items-start gap-3">
    <span className="mt-0.5 flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10 text-primary">
      {icon}
    </span>
    <div>
      <h2 className="font-display text-base font-bold text-foreground">{title}</h2>
      {subtitle && <p className="mt-0.5 text-xs text-muted-foreground">{subtitle}</p>}
    </div>
  </div>
);

const StatCard = ({
  icon, value, label, tone,
}: { icon: ReactNode; value: ReactNode; label: string; tone: "primary" | "accent" }) => (
  <div className="relative overflow-hidden rounded-2xl border border-white/10 bg-card/40 p-5 backdrop-blur-xl transition-all hover:-translate-y-0.5">
    <div
      aria-hidden
      className={cn(
        "pointer-events-none absolute inset-0 bg-gradient-to-br",
        tone === "primary"
          ? "from-primary/10 via-transparent to-accent/5"
          : "from-accent/10 via-transparent to-primary/5",
      )}
    />
    <div className="relative">
      <div
        className={cn(
          "flex h-10 w-10 items-center justify-center rounded-xl ring-1",
          tone === "primary"
            ? "bg-primary/15 text-primary ring-primary/20"
            : "bg-accent/15 text-accent ring-accent/20",
        )}
      >
        {icon}
      </div>
      <p className="font-display mt-3 text-3xl font-bold tabular-nums text-foreground">{value}</p>
      <p className="font-display mt-1 text-[10px] font-semibold uppercase tracking-[0.22em] text-muted-foreground">
        {label}
      </p>
    </div>
  </div>
);

const Pill = ({
  active, onClick, children,
}: { active: boolean; onClick: () => void; children: ReactNode }) => (
  <button
    type="button"
    onClick={onClick}
    className={cn(
      "rounded-full border px-3 py-1.5 text-sm font-medium transition-all",
      active
        ? "border-primary bg-primary text-primary-foreground shadow-[0_0_18px_-6px_hsl(var(--primary))]"
        : "border-white/15 bg-white/[0.03] text-foreground hover:border-primary/50 hover:bg-white/[0.06]",
    )}
  >
    {children}
  </button>
);

const FieldGroup = ({ label, children }: { label: string; children: ReactNode }) => (
  <div className="space-y-2">
    <span className="block text-sm font-medium leading-none">{label}</span>
    <div className="flex flex-wrap gap-2">{children}</div>
  </div>
);

const SupportRow = ({
  icon, title, subtitle, onClick, to,
}: { icon: ReactNode; title: string; subtitle: string; onClick?: () => void; to?: string }) => {
  const className =
    "flex w-full items-center gap-3 rounded-xl border border-white/10 bg-white/[0.02] p-3 text-left transition-colors hover:border-primary/40 hover:bg-white/[0.05]";
  const inner = (
    <>
      <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/10 text-primary">
        {icon}
      </div>
      <div className="min-w-0">
        <p className="text-sm font-semibold text-foreground">{title}</p>
        <p className="truncate text-xs text-muted-foreground">{subtitle}</p>
      </div>
    </>
  );
  if (to) return <Link to={to} className={className}>{inner}</Link>;
  return <button type="button" onClick={onClick} className={className}>{inner}</button>;
};

const FooterLink = ({ to, icon, label }: { to: string; icon: ReactNode; label: string }) => (
  <Link
    to={to}
    className="group flex flex-col items-center justify-center gap-2 rounded-xl border border-white/10 bg-white/[0.02] p-4 text-muted-foreground transition-all hover:border-primary/40 hover:bg-primary/5 hover:text-primary"
  >
    <span className="flex h-8 w-8 items-center justify-center rounded-full bg-white/[0.04] group-hover:bg-primary/15">
      {icon}
    </span>
    <span className="text-xs font-medium">{label}</span>
  </Link>
);

// ThemeToggle removed — app is intentionally always-dark.

